import NewsletterPreview from "./NewsletterPreview";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// Hook to detect screen size
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState<"mobile" | "tablet" | "desktop">(
    "desktop"
  );

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setScreenSize("mobile");
      } else if (width < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return screenSize;
};

interface NewsletterData {
  searchText: string;
  emailSubject: string;
  emailBody: string[];
  senderName?: string;
  senderEmail?: string;
  avatarLetter?: string;
}

interface SimpleStackedPreviewProps {
  newsletters: [NewsletterData, NewsletterData, NewsletterData, NewsletterData];
}

const SimpleStackedPreview = ({ newsletters }: SimpleStackedPreviewProps) => {
  const [currentNewsletters, setCurrentNewsletters] = useState(newsletters);
  const [isHovered, setIsHovered] = useState(false);
  const screenSize = useScreenSize();

  // Responsive stack configuration
  const getStackConfig = () => {
    switch (screenSize) {
      case "mobile":
        return {
          CARD_OFFSET: 12, // Smaller spacing for mobile
          SCALE_FACTOR: 0.08, // More dramatic scaling for visibility
          ROTATION: { front: -0.5, middle: 1, back: -1.5 }, // Subtle rotation
          OFFSET: { x: 8, y: 8 }, // Smaller position offsets
        };
      case "tablet":
        return {
          CARD_OFFSET: 16,
          SCALE_FACTOR: 0.06,
          ROTATION: { front: -0.8, middle: 1.5, back: -2.5 },
          OFFSET: { x: 12, y: 12 },
        };
      default: // desktop
        return {
          CARD_OFFSET: 20,
          SCALE_FACTOR: 0.05,
          ROTATION: { front: -1, middle: 2, back: -3 },
          OFFSET: { x: 15, y: 15 },
        };
    }
  };

  const config = getStackConfig();

  // Rotate newsletters every 3 seconds (paused on hover)
  useEffect(() => {
    if (isHovered) return; // Pause rotation when hovered

    const interval = setInterval(() => {
      setCurrentNewsletters((prev) => {
        // Move last item to front: [A, B, C] → [C, A, B]
        const newArray = [...prev];
        newArray.unshift(newArray.pop() as NewsletterData);
        return newArray as [
          NewsletterData,
          NewsletterData,
          NewsletterData,
          NewsletterData
        ];
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovered]);

  return (
    <div
      className="relative w-full max-w-sm  sm:max-w-md md:max-w-lg lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl mx-auto p-4 sm:p-6 md:p-8 mt-8 lg:mt-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background container with speckled pattern */}
      <div className="absolute inset-0 speckled-bg rounded-2xl sm:rounded-3xl "></div>

      {/* Stacked cards container */}
      <div className="relative h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px]">
        {currentNewsletters.map((newsletter, index) => {
          const isFront = index === 0;
          const isMiddle = index === 1;

          return (
            <motion.div
              key={newsletter.emailSubject}
              className="absolute top-0 left-0 w-full"
              style={{
                transformOrigin: "top center",
              }}
              animate={{
                top: index * config.CARD_OFFSET,
                scale: 1 - index * config.SCALE_FACTOR,
                zIndex: currentNewsletters.length - index,
                rotate: isFront
                  ? config.ROTATION.front
                  : isMiddle
                  ? config.ROTATION.middle
                  : config.ROTATION.back,
                opacity: isFront ? 1 : isMiddle ? 0.8 : 0.6,
                x: isFront
                  ? 0
                  : isMiddle
                  ? config.OFFSET.x
                  : config.OFFSET.x * 2,
                y: isFront
                  ? 0
                  : isMiddle
                  ? config.OFFSET.y
                  : config.OFFSET.y * 2,
              }}
              transition={{
                duration: 0.8,
                ease: "easeInOut",
              }}
            >
              <NewsletterPreview {...newsletter} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default SimpleStackedPreview;
